import { useState, useEffect, useCallback, useRef } from 'react'
import { FileText, RefreshCw, Users, DollarSign, Target, Filter, TrendingUp, Download } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { pisteirosService } from '../../services/pisteirosService'
import leiloesService from '../../services/leiloesService'
import toast from 'react-hot-toast'

const slug = (t) =>
  String(t || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '')

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export default function RelatorioReceitaPisteiros() {
  const { organizadorAtivo } = useOrganizador()
  const navigate = useNavigate()
  const orgId = organizadorAtivo?.id
  const moduloAtivo = organizadorAtivo?.produtos_premium?.includes('quem_vendeu_trouxe')
  const iframeRef = useRef(null)

  const [leiloes, setLeiloes] = useState([])
  const [pisteiros, setPisteiros] = useState([])
  const [filtros, setFiltros] = useState(() => {
    // Inicializa com "Este mês" e "Só Quem Vendeu"
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    return {
      leilao_id: '',
      data_inicio: start.toISOString().slice(0, 10),
      data_fim: now.toISOString().slice(0, 10),
      pisteiro_id: '',
      situacao: 'vendeu',
      ordenar_por: 'numero_lote'
    }
  })
  const [loading, setLoading] = useState(false)
  const [dados, setDados] = useState(null)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [pdfNome, setPdfNome] = useState('relatorio_receita_pisteiros.pdf')
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)

  useEffect(() => {
    if (organizadorAtivo && !moduloAtivo) {
      navigate('/dashboard', { replace: true })
    }
  }, [organizadorAtivo, moduloAtivo, navigate])

  useEffect(() => {
    if (!orgId) return
    leiloesService.getByOrganizador(orgId)
      .then(res => setLeiloes(res.data || res || []))
      .catch(() => {})
    pisteirosService.listar(orgId)
      .then(res => setPisteiros(res.data || res || []))
      .catch(() => {})
  }, [orgId])

  const carregar = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const params = {}
      const modo = filtros.modo || 'periodo'
      if (modo === 'leilao' && filtros.leilao_id) {
        params.leilao_id = filtros.leilao_id
      } else {
        if (filtros.data_inicio) params.data_inicio = filtros.data_inicio
        if (filtros.data_fim) params.data_fim = filtros.data_fim
      }
      if (filtros.pisteiro_id) params.pisteiro_id = filtros.pisteiro_id
      if (filtros.situacao && filtros.situacao !== 'ambos') params.situacao = filtros.situacao
      if (filtros.ordenar_por) params.ordenar_por = filtros.ordenar_por
      const res = await pisteirosService.relatorio(orgId, params)
      setDados(res.data || res)
    } catch {
      toast.error('Erro ao carregar relatório')
    } finally {
      setLoading(false)
    }
  }, [orgId, filtros])

  useEffect(() => {
    if (orgId && moduloAtivo) carregar()
  }, [orgId, moduloAtivo]) // eslint-disable-line

  const handleExportarPdf = async () => {
    if (!orgId) return
    setLoadingPdf(true)
    try {
      const params = {}
      const modo = filtros.modo || 'periodo'
      if (modo === 'leilao' && filtros.leilao_id) {
        params.leilao_id = filtros.leilao_id
      } else {
        if (filtros.data_inicio) params.data_inicio = filtros.data_inicio
        if (filtros.data_fim) params.data_fim = filtros.data_fim
      }
      if (filtros.pisteiro_id) params.pisteiro_id = filtros.pisteiro_id
      if (filtros.situacao && filtros.situacao !== 'ambos') params.situacao = filtros.situacao
      if (filtros.ordenar_por) params.ordenar_por = filtros.ordenar_por
      const res = await pisteirosService.relatorioPdf(orgId, params)
      const blob = new Blob([res.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(blob)
      setPdfUrl(url)
      let sufixo
      if (modo === 'leilao' && filtros.leilao_id) {
        const l = leiloes.find(x => String(x.id) === String(filtros.leilao_id))
        sufixo = slug(l?.nome || l?.titulo) || `leilao_${filtros.leilao_id}`
      } else {
        const br = (d) => (d ? d.split('-').reverse().join('-') : '')
        sufixo = `${br(filtros.data_inicio)}_a_${br(filtros.data_fim)}`
      }
      setPdfNome(`receita_pisteiros_${sufixo}.pdf`)
      setShowPdfModal(true)
    } catch {
      toast.error('Erro ao gerar PDF')
    } finally {
      setLoadingPdf(false)
    }
  }

  const closePdfModal = () => {
    setShowPdfModal(false)
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) }
  }

  const printPdf = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus()
      iframeRef.current.contentWindow.print()
    }
  }

  const itens = dados?.dados || dados?.itens || dados?.pisteiros || []
  const totais = dados?.totais || {}

  const totalGeral = totais.total_geral || itens.reduce((s, i) => s + Number(i.total || i.valor_trouxe || 0) + Number(i.valor_vendeu || 0), 0)
  const totalTrouxe = totais.total_trouxe || itens.reduce((s, i) => s + Number(i.valor_trouxe || 0), 0)
  const totalVendeu = totais.total_vendeu || itens.reduce((s, i) => s + Number(i.valor_vendeu || 0), 0)
  const totalQtd = itens.reduce((s, i) => s + (i.qtd_trouxe || 0) + (i.qtd_vendeu || 0), 0)

  if (!moduloAtivo) return null

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-xl px-6 py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/20 rounded-xl">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Receita Pisteiros</h1>
              <p className="text-purple-200 text-sm">Relatório de receita por pisteiro</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={carregar}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-white/15 hover:bg-white/25 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              Atualizar
            </button>
            <button
              onClick={handleExportarPdf}
              disabled={loadingPdf || !dados}
              className="flex items-center gap-2 bg-white text-purple-700 px-4 py-2 rounded-lg font-semibold text-sm hover:bg-purple-50 transition-colors disabled:opacity-50 shadow-sm"
            >
              <Download size={15} />
              {loadingPdf ? 'Gerando...' : 'Exportar PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Cards resumo */}
      {dados && !loading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 bg-purple-100 rounded-lg"><Target size={14} className="text-purple-600" /></div>
              <span className="text-xs font-medium text-gray-500 uppercase">Pisteiros</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{itens.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 bg-blue-100 rounded-lg"><Users size={14} className="text-blue-600" /></div>
              <span className="text-xs font-medium text-gray-500 uppercase">Lotes</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{totalQtd}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 bg-green-100 rounded-lg"><DollarSign size={14} className="text-green-600" /></div>
              <span className="text-xs font-medium text-gray-500 uppercase">Total Geral</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{fmt(totalGeral)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 bg-amber-100 rounded-lg"><TrendingUp size={14} className="text-amber-600" /></div>
              <span className="text-xs font-medium text-gray-500 uppercase">Média/Pisteiro</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{itens.length > 0 ? fmt(totalGeral / itens.length) : 'R$ 0,00'}</p>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Filtrar por</label>
            <select
              value={filtros.modo || 'periodo'}
              onChange={e => setFiltros(p => ({ ...p, modo: e.target.value, leilao_id: e.target.value === 'leilao' ? p.leilao_id : '' }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="periodo">Por Período</option>
              <option value="leilao">Por Leilão</option>
            </select>
          </div>

          {(filtros.modo || 'periodo') === 'periodo' ? (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Data Início</label>
                <input
                  type="date"
                  value={filtros.data_inicio}
                  onChange={e => setFiltros(p => ({ ...p, data_inicio: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Data Fim</label>
                <input
                  type="date"
                  value={filtros.data_fim}
                  onChange={e => setFiltros(p => ({ ...p, data_fim: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </>
          ) : (
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Leilão</label>
              <select
                value={filtros.leilao_id}
                onChange={e => setFiltros(p => ({ ...p, leilao_id: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Selecione o leilão</option>
                {leiloes.map(l => (
                  <option key={l.id} value={l.id}>{l.nome || l.titulo}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Pisteiro</label>
            <select
              value={filtros.pisteiro_id}
              onChange={e => setFiltros(p => ({ ...p, pisteiro_id: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Todos</option>
              {pisteiros.map(p => (
                <option key={p.id} value={p.id}>{p.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Tipo</label>
            <select
              value={filtros.situacao}
              onChange={e => setFiltros(p => ({ ...p, situacao: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="ambos">Captou + Vendeu</option>
              <option value="trouxe">Só Quem Captou</option>
              <option value="vendeu">Só Quem Vendeu</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Ordenar por</label>
            <select
              value={filtros.ordenar_por}
              onChange={e => setFiltros(p => ({ ...p, ordenar_por: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="numero_lote">Nº do Lote</option>
              <option value="data_compra">Data da Compra</option>
              <option value="comprador">Comprador</option>
            </select>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            onClick={carregar}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 shadow-sm"
          >
            <Filter size={14} />
            {loading ? 'Buscando...' : 'Aplicar Filtros'}
          </button>
        </div>
      </div>

      {/* Tabelas */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
          <div className="w-8 h-8 border-3 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Carregando dados...</p>
        </div>
      ) : itens.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
          <Users className="w-14 h-14 mx-auto mb-3 text-gray-200" />
          <p className="font-medium text-gray-700">Nenhum dado encontrado</p>
          <p className="text-sm text-gray-400 mt-1">Ajuste os filtros ou selecione outro período.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {itens.filter(i => (i.detalhes_vendeu || []).length > 0 || (i.qtd_trouxe || 0) > 0).map((item) => {
            const detalhes = item.detalhes_vendeu || []
            const totalQtdPisteiro = detalhes.reduce((s, d) => s + (d.qtd || 0), 0)
            const totalComCompra = detalhes.reduce((s, d) => s + Number(d.com_compra || 0), 0)
            const totalVlComissao = detalhes.reduce((s, d) => s + Number(d.vl_comissao || 0), 0)

            return (
              <div key={`det-${item.pisteiro_id}`} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <p className="font-bold text-gray-900">Pisteiro: {item.nome}</p>
                  <p className="text-xs text-gray-500 uppercase">Comissão: {Number(item.percentual_venda || 0).toFixed(2)}%</p>
                </div>

                {detalhes.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50/80">
                        <tr>
                          <th className="text-left px-4 py-2.5 font-semibold text-gray-600 text-xs uppercase">Lote</th>
                          <th className="text-left px-4 py-2.5 font-semibold text-gray-600 text-xs uppercase">Comprador</th>
                          <th className="text-center px-4 py-2.5 font-semibold text-gray-600 text-xs uppercase">Data Compra</th>
                          <th className="text-center px-4 py-2.5 font-semibold text-gray-600 text-xs uppercase">Qtd</th>
                          <th className="text-right px-4 py-2.5 font-semibold text-gray-600 text-xs uppercase">Com. Compra</th>
                          <th className="text-right px-4 py-2.5 font-semibold text-gray-600 text-xs uppercase">Vl. Comissão</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {detalhes.map((d, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-2.5 font-medium text-gray-900">{d.numero_lote}</td>
                            <td className="px-4 py-2.5">
                              <div className="font-medium text-gray-900">{d.comprador}</div>
                              {d.descricao && <div className="text-xs text-gray-400">{d.descricao}</div>}
                            </td>
                            <td className="px-4 py-2.5 text-center text-gray-600">
                              {d.data_compra ? new Date(d.data_compra).toLocaleDateString('pt-BR') : '-'}
                            </td>
                            <td className="px-4 py-2.5 text-center text-gray-700 font-medium">{d.qtd}</td>
                            <td className="px-4 py-2.5 text-right text-gray-700">{fmt(d.com_compra)}</td>
                            <td className="px-4 py-2.5 text-right font-bold text-gray-900">{fmt(d.vl_comissao)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                        <tr className="font-bold text-sm">
                          <td className="px-4 py-2.5 text-gray-700" colSpan={2}>Quantidade de Lotes: {detalhes.length}</td>
                          <td className="px-4 py-2.5"></td>
                          <td className="px-4 py-2.5 text-center text-gray-700">{totalQtdPisteiro}</td>
                          <td className="px-4 py-2.5 text-right text-gray-700">{fmt(totalComCompra)}</td>
                          <td className="px-4 py-2.5 text-right text-gray-900">{fmt(totalVlComissao)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="px-5 py-4 text-sm text-gray-500">
                    {item.qtd_trouxe > 0 && (
                      <p>Captou {item.qtd_trouxe} lote(s) — Total: {fmt(item.valor_trouxe)}</p>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {/* Total Geral */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl px-6 py-4 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-300">Total Geral</span>
            <span className="text-xl font-black text-white">{fmt(totalGeral)}</span>
          </div>
        </div>
      )}

      {/* Modal PDF */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closePdfModal} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800">Receita Pisteiros</h3>
              <div className="flex items-center gap-2">
                <a
                  href={pdfUrl}
                  download={pdfNome}
                  className="px-3 py-1.5 text-sm border border-purple-600 text-purple-700 rounded-lg hover:bg-purple-50 font-medium flex items-center gap-1.5"
                >
                  <Download size={14} />
                  Baixar PDF
                </a>
                <button onClick={printPdf} className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">
                  Imprimir
                </button>
                <button onClick={closePdfModal} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                  Fechar
                </button>
              </div>
            </div>
            <div className="flex-1 p-2">
              <iframe ref={iframeRef} src={pdfUrl} className="w-full h-full rounded border" title="PDF Receita Pisteiros" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}